<!-- "Released" tab of the video dialog, drawn inside #videoPub (VideoDialog.vue) again from the start every time the tab
     is opened; shown for a published video or one in "Pronto/Pubblicato" (video store pubVisible). Release date and time
     (video.publishedAt), "Paste a link" (the social is recognised from the domain, pubDetect), one link per social
     (video.pubLinks, http/https only, PUB_SOCIALS) and notes (video.pubNote). The draft is the video store's `pub`,
     saved with "Save video". Like before, a social's row lights up (with "Open" and ×) when its field is left, a link is
     pasted or removed, not while typing. Logic in src/features/published.ts (pubUrl keeps only http/https addresses,
     so the "Open" link needs no other check). -->
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from '../../composables/useI18n'
import { useVideoStore } from '../../stores/video'
import { PUB_SOCIALS, pubPasted, pubTyped, pubUrl } from '../../features/published'
import { esc } from '../../utils/html'
import MentionField from '../fields/MentionField.vue'

const { t } = useI18n()
const video = useVideoStore()
const p = video.pub
const inp = 'w-full bg-darkinput border border-gray-800 rounded-lg px-3 py-2.5 text-gray-100 focus:outline-none focus:border-brand transition select-text'

/** The links as last drawn: which rows are lit, with "Open" and × */
const shown = ref<Record<string, string>>({ ...p.links })
/** Message under "Paste a link" (HTML) */
const msg = ref('')
function redraw() {
  shown.value = { ...p.links }
  msg.value = ''
}
const intro = computed(() => (video.published
  ? t('<b class="text-gray-100">Published video details.</b> The links you add here become buttons on its card in the Published videos page.')
  : t('<b class="text-gray-100">The video is ready.</b> When it comes out, add here where you uploaded it, then press <b>Mark as published</b> in the Details tab.')))

const pasteBox = ref<HTMLInputElement | null>(null)
function paste() {
  const el = pasteBox.value
  if (!el) return
  const r = pubPasted(el.value)
  if (!r) { msg.value = el.value.trim() ? esc(t("This doesn't look like a link (it must be a web address).")) : ''; return }
  p.links[r.key] = r.url
  el.value = ''
  redraw()
  const name = (PUB_SOCIALS.find(x => x[0] === r.key) as (typeof PUB_SOCIALS)[number])[1]
  msg.value = `<i class="fas fa-check text-green-400 mr-1"></i>${t('Added to <b>{name}</b>.', { name: esc(name) })}`
}
function pasted() { setTimeout(paste) }
function typed(k: string, e: Event) { p.links[k] = (e.target as HTMLInputElement).value }
function done(k: string, e: Event) {
  const el = e.target as HTMLInputElement
  p.links[k] = pubTyped(el.value)
  el.value = p.links[k]
  redraw()
}
function remove(k: string) {
  p.links[k] = ''
  redraw()
}
const iconStyle = (col: string) => (col ? 'color:' + col : '')
</script>

<template>
  <div :class="['flex gap-3 p-4 rounded-2xl border', video.published ? 'border-green-400/30 bg-green-400/10' : 'border-gray-800 bg-darkinput']">
    <i :class="['fas fa-flag-checkered', video.published ? 'text-green-400' : 'text-brand', 'mt-0.5']"></i>
    <div class="text-xs text-gray-300 leading-relaxed" v-html="intro"></div></div>
  <div><label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('RELEASED ON') }}</label>
    <input v-model="p.at" type="datetime-local" :class="[inp, 'sm:w-auto']"></div>
  <div><label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('PASTE A LINK') }}</label>
    <div class="flex gap-2"><input id="pubPasteInp" ref="pasteBox" type="url" :placeholder="t('Paste the link: I\'ll put it in the right social by myself')" :class="inp" @paste="pasted" @keydown.enter.prevent="paste">
      <button type="button" class="px-3 rounded-lg bg-brand hover:bg-brandDark text-white text-xs font-bold whitespace-nowrap" @click="paste">{{ t('Add') }}</button></div>
    <div id="pubPasteMsg" class="text-[11px] text-gray-400 mt-1.5 min-h-[1rem]" v-html="msg"></div></div>
  <div><label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('WHERE IT WAS UPLOADED') }}</label>
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
      <div v-for="[k, name, ic, col] in PUB_SOCIALS" :key="k" :class="['flex items-center gap-2 p-2 rounded-xl border', pubUrl(shown[k]) ? 'border-brand/40 bg-brand/5' : 'border-gray-800']">
        <span class="w-9 h-9 rounded-lg bg-darkbtn flex items-center justify-center flex-shrink-0"><i :class="[ic, { 'text-gray-100': !col }]" :style="iconStyle(col)" :aria-label="name"></i></span>
        <input type="url" :value="p.links[k] || ''" :placeholder="name" class="flex-1 min-w-0 bg-transparent text-sm text-gray-100 focus:outline-none select-text" @input="typed(k, $event)" @change="done(k, $event)">
        <template v-if="pubUrl(shown[k])">
          <a :href="pubUrl(shown[k])" target="_blank" rel="noopener" :title="t('Open')" class="w-8 h-8 flex items-center justify-center rounded-md text-gray-400 hover:text-brand"><i class="fas fa-arrow-up-right-from-square text-xs"></i></a>
          <button type="button" :title="t('Remove')" class="w-8 h-8 flex items-center justify-center rounded-md text-gray-500 hover:text-brand" @click="remove(k)"><i class="fas fa-xmark text-xs"></i></button>
        </template></div>
    </div></div>
  <div><label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('PUBLISHING NOTES') }}</label>
    <MentionField id="pubNote" v-model="p.note" :placeholder="t('E.g. also uploaded as a Short, collaboration with…, sponsor…')" class="min-h-[5rem] w-full bg-darkinput border border-gray-800 rounded-lg p-3 text-gray-100 transition" /></div>
  <p class="text-[11px] text-gray-500" v-html="t('Changes are saved with <b>Save video</b>.')"></p>
</template>
